import { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';
import {
  fetchPageContent,
  upsertSection,
  type PageSection,
} from '../../../utils/pageContentStorage';
import { supabase } from '../../../lib/supabase';

const PAGE_GROUPS = [
  { key: 'home', label: '首頁', icon: 'ri-home-4-line' },
  { key: 'about', label: '關於我們', icon: 'ri-information-line' },
  { key: 'iphone', label: 'iPhone 維修', icon: 'ri-smartphone-line' },
  { key: 'ipad', label: 'iPad 維修', icon: 'ri-tablet-line' },
  { key: 'mac', label: 'Mac 維修', icon: 'ri-macbook-line' },
  { key: 'cases', label: '維修案例', icon: 'ri-tools-line' },
  { key: 'contact', label: '聯絡我們', icon: 'ri-map-pin-line' },
];

const LONG_CONTENT_SECTIONS = ['story'];

export default function AdminPagesPage() {
  const [data, setData] = useState<PageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState('home');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PageSection | null>(null);
  const [toast, setToast] = useState('');
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const [imgUploading, setImgUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const content = await fetchPageContent();
      setData(content);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const groupSections = data.filter(s => s.pageKey === activeGroup);

  const handleEdit = (section: PageSection) => {
    setEditingId(section.id);
    setEditForm({ ...section });
    setPreviewImg(section.image ?? null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm(null);
    setPreviewImg(null);
  };

  const handleSave = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      await upsertSection(editForm);
      setData(prev => prev.map(s => s.id === editForm.id ? editForm : s));
      setEditingId(null);
      setEditForm(null);
      setPreviewImg(null);
      showToast('已儲存至資料庫！變更立即生效');
    } catch {
      showToast('儲存失敗，請重試');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUrlChange = (url: string) => {
    if (!editForm) return;
    setEditForm({ ...editForm, image: url });
    setPreviewImg(url);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editForm) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { showToast('僅支援 JPG、PNG、WebP、GIF 格式'); return; }
    if (file.size > 10 * 1024 * 1024) { showToast('圖片大小不能超過 10MB'); return; }
    setImgUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `pages-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('news-images')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('news-images').getPublicUrl(fileName);
      handleImageUrlChange(urlData.publicUrl);
      showToast('圖片上傳成功');
    } catch (err) {
      console.error('Upload failed:', err);
      showToast('圖片上傳失敗，請重試');
    } finally {
      setImgUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isLongContent = (section: PageSection) => LONG_CONTENT_SECTIONS.includes(section.sectionKey);
  const isContactInfo = (section: PageSection) =>
    section.sectionKey.startsWith('contact-') ||
    ['address', 'phone', 'email', 'hours'].includes(section.sectionKey);

  return (
    <AdminLayout>
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-gray-900 text-white text-sm px-5 py-3 rounded-xl">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">頁面圖文管理</h1>
          <p className="text-gray-500 text-sm mt-1">修改各頁面的圖片、標題與文字內容，儲存後立即生效</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 border border-green-100 px-3 py-1.5 rounded-full">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-database-2-line text-green-500" />
          </div>
          資料儲存於 Supabase
        </div>
      </div>

      {/* Page Group Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {PAGE_GROUPS.map(g => (
          <button
            key={g.key}
            onClick={() => { setActiveGroup(g.key); setEditingId(null); setEditForm(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
              activeGroup === g.key
                ? 'bg-orange-500 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-500'
            }`}
          >
            <i className={`${g.icon} text-sm`} />
            {g.label}
          </button>
        ))}
      </div>

      {/* Hint */}
      <div className="flex items-start gap-2 mb-5 bg-orange-50 border border-orange-100 rounded-xl px-4 py-3">
        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
          <i className="ri-lightbulb-line text-orange-500 text-sm" />
        </div>
        <p className="text-orange-700 text-xs">
          點擊「編輯」即可修改該區塊的圖片或文字，儲存後前台頁面馬上反映。<br />
          <strong>圖片</strong>：可直接上傳圖片檔案，或貼上圖片網址。所有資料儲存於 Supabase 資料庫，永久保存。
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-400 text-sm gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin" />
          </div>
          載入中...
        </div>
      )}

      {/* Sections List */}
      {!loading && (
        <div className="space-y-4">
          {groupSections.length === 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400 text-sm">
              此頁面目前沒有可編輯的區塊
            </div>
          )}

          {groupSections.map(section => {
            const isEditing = editingId === section.id;
            const long = isLongContent(section);
            const contactInfo = isContactInfo(section);

            return (
              <div key={section.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                {/* Section Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-xl">
                      {contactInfo
                        ? <i className="ri-map-pin-line text-orange-500 text-sm" />
                        : long
                          ? <i className="ri-article-line text-orange-500 text-sm" />
                          : <i className="ri-layout-line text-orange-500 text-sm" />
                      }
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900 text-sm">{section.label}</span>
                      {long && <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">長文</span>}
                      {contactInfo && section.sectionKey !== 'banner' && (
                        <span className="ml-2 text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">聯絡資訊</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => isEditing ? handleCancel() : handleEdit(section)}
                    className={`px-4 py-1.5 text-xs rounded-lg cursor-pointer whitespace-nowrap font-medium transition-colors ${
                      isEditing
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-orange-50 text-orange-500 hover:bg-orange-100'
                    }`}
                  >
                    {isEditing ? '取消編輯' : '編輯'}
                  </button>
                </div>

                {/* Preview (not editing) */}
                {!isEditing && (
                  <div className="px-6 py-4 flex gap-6 items-start">
                    {section.image && (
                      <div className="w-40 h-24 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
                        <img src={section.image} alt="" className="w-full h-full object-cover object-top" />
                      </div>
                    )}
                    <div className="flex-1 space-y-1.5">
                      {section.title && (
                        <div>
                          <span className="text-xs text-gray-400 font-medium">
                            {contactInfo && section.sectionKey !== 'banner' ? '內容：' : '標題：'}
                          </span>
                          <span className="text-sm text-gray-700">{section.title}</span>
                        </div>
                      )}
                      {section.subtitle && (
                        <div>
                          <span className="text-xs text-gray-400 font-medium">
                            {contactInfo && section.sectionKey !== 'banner' ? '備註：' : '副標題：'}
                          </span>
                          <span className="text-sm text-gray-600">{section.subtitle}</span>
                        </div>
                      )}
                      {section.content && (
                        <div>
                          <span className="text-xs text-gray-400 font-medium">內文：</span>
                          <span className="text-sm text-gray-600 line-clamp-2">{section.content}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Edit Form */}
                {isEditing && editForm && (
                  <div className="px-6 py-5 space-y-5">

                    {/* Image Field */}
                    {'image' in section && section.image !== undefined && (
                      <div>
                        <label className="text-xs font-semibold text-gray-600 mb-2 block">背景 / 橫幅圖片</label>
                        <div className="flex gap-4 items-start">
                          <div className="flex-1 space-y-2">
                            <label className={`flex items-center gap-2 w-full border-2 border-dashed border-gray-200 hover:border-orange-400 rounded-xl px-4 py-3 cursor-pointer transition-colors ${imgUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                              <i className="ri-upload-cloud-2-line text-orange-400 text-lg" />
                              <span className="text-sm text-gray-500">
                                {imgUploading ? '上傳中...' : '點擊上傳圖片（JPG / PNG / WebP，最大 10MB）'}
                              </span>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={handleImageUpload}
                              />
                            </label>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400 whitespace-nowrap">或貼上網址</span>
                              <input
                                value={editForm.image ?? ''}
                                onChange={e => handleImageUrlChange(e.target.value)}
                                placeholder="https://..."
                                className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-orange-400"
                              />
                            </div>
                            <p className="text-xs text-gray-400">建議使用 1920×500 或更大尺寸的橫幅圖片</p>
                          </div>
                          {previewImg && (
                            <div className="w-32 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 relative">
                              <img
                                src={previewImg}
                                alt="預覽"
                                className="w-full h-full object-cover object-top"
                                onError={() => setPreviewImg(null)}
                              />
                              <button
                                type="button"
                                onClick={() => { setPreviewImg(null); setEditForm(f => f ? { ...f, image: '' } : f); }}
                                className="absolute top-1 right-1 w-5 h-5 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center cursor-pointer"
                              >
                                <i className="ri-close-line text-xs" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Title Field */}
                    {'title' in section && section.title !== undefined && (
                      <div>
                        <label className="text-xs font-semibold text-gray-600 mb-2 block">
                          {contactInfo && section.sectionKey !== 'banner' ? '內容' : '標題'}
                        </label>
                        <input
                          value={editForm.title ?? ''}
                          onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                          placeholder={contactInfo && section.sectionKey !== 'banner' ? '如：台北市信義區信義路五段7號' : '頁面標題'}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400"
                        />
                      </div>
                    )}

                    {/* Subtitle Field */}
                    {'subtitle' in section && section.subtitle !== undefined && (
                      <div>
                        <label className="text-xs font-semibold text-gray-600 mb-2 block">
                          {contactInfo && section.sectionKey !== 'banner' ? '備註說明' : '副標題'}
                        </label>
                        <input
                          value={editForm.subtitle ?? ''}
                          onChange={e => setEditForm({ ...editForm, subtitle: e.target.value })}
                          placeholder={contactInfo && section.sectionKey !== 'banner' ? '如：近台北101/世貿站' : '頁面副標題'}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400"
                        />
                      </div>
                    )}

                    {/* Content Field */}
                    {'content' in section && section.content !== undefined && (
                      <div>
                        <label className="text-xs font-semibold text-gray-600 mb-2 block">
                          {long ? '內文（可分段，段落間空一行）' : '內文'}
                        </label>
                        <textarea
                          value={editForm.content ?? ''}
                          onChange={e => setEditForm({ ...editForm, content: e.target.value })}
                          rows={long ? 12 : 5}
                          placeholder={long ? '輸入內文，段落之間空一行即可自動換段…' : '頁面內文...'}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-orange-400 resize-y leading-relaxed"
                        />
                        {long && (
                          <p className="text-xs text-gray-400 mt-1.5">段落之間按兩次 Enter 空一行，前台會自動分段顯示</p>
                        )}
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors flex items-center gap-2"
                      >
                        {saving && <i className="ri-loader-4-line animate-spin text-sm" />}
                        {saving ? '儲存中...' : '儲存至資料庫'}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-xl cursor-pointer whitespace-nowrap"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
